import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './asset-wos.style.js';
import { getWorkOrdersByAsset, getUsersByCompany } from '@cmms/api';
import { db } from '../../../../services/auth.service.js';
import { WorkOrderStatusCat, WorkOrderPriorityCat } from '@cmms/models';



@customElement('asset-wos')
export class AssetWOs extends LitElement {
  @property({ type: Object }) asset: any = null;
  @property({ type: String }) companyId: string = '';

  @state() private workOrders: Array<any> = [];
  @state() private users: Array<any> = [];
  @state() private isLoading = false;
  @state() private error: string | null = null;

  static styles = styles;

  async connectedCallback() {
    super.connectedCallback();
    await this.loadUsers();
    await this.loadWorkOrders();
  }

  async updated(changed: Map<string, unknown>) {
    if (changed.has('asset') && this.asset) {
      await this.loadWorkOrders();
    }
  }

  private async loadUsers() {
    if (!this.companyId) return;
    
    try {
      const result = await getUsersByCompany(db, this.companyId);
      this.users = Array.isArray(result) ? result : (result?.data || []);
    } catch (error) {
      console.log('Error loading users:', error);
      this.users = [];
    }
  }

  private async loadWorkOrders() {
    if (!this.asset) return;

    const ids = Array.from(new Set([
      this.asset?.id,
      this.asset?.asset_id,
      this.asset?.model?.asset_id
    ].filter(Boolean).map((v: any) => String(v))));

    if (ids.length === 0) {
      this.workOrders = [];
      return;
    }

    try {
      this.isLoading = true;
      this.error = null;
      const collected: any[] = [];

      for (const id of ids) {
        const resp: any = await getWorkOrdersByAsset(db, id);
        const raw = Array.isArray(resp) ? resp : (resp?.data || []);
        if (raw && raw.length) collected.push(...raw);
      }

      this.workOrders = collected;
    } catch (e: any) {
      this.error = e?.message || 'Failed to load work orders';
      this.workOrders = [];
    } finally {
      this.isLoading = false;
    }
  }

  private formatDate(ts?: number | null) {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString();
  }

  private getUserName(userId?: string) {
    if (!userId) return 'Unassigned';
    const user = this.users.find(u => u.id === userId || u.$id === userId || u.user_id === userId || u.uid === userId);
    return user?.model?.name || user?.model?.display_name || user?.name || user?.display_name || user?.email || `User ${userId}`;
  }

  private getStatusLabel(statusCat?: number) {
    switch (statusCat) {
      case WorkOrderStatusCat.OPEN: return 'Open';
      case WorkOrderStatusCat.IN_PROGRESS: return 'In Progress';
      case WorkOrderStatusCat.COMPLETED: return 'Completed';
      case WorkOrderStatusCat.AWAITING_PARTS: return 'Awaiting Parts';
      default: return 'Unknown';
    }
  }

  private getStatusClass(statusCat?: number) {
    switch (statusCat) {
      case WorkOrderStatusCat.OPEN: return 'open';
      case WorkOrderStatusCat.IN_PROGRESS: return 'in-progress';
      case WorkOrderStatusCat.COMPLETED: return 'completed';
      case WorkOrderStatusCat.AWAITING_PARTS: return 'awaiting-parts';
      default: return 'unknown';
    }
  }

  private getPriorityLabel(priorityCat?: number) {
    switch (priorityCat) {
      case WorkOrderPriorityCat.LOW: return 'Low';
      case WorkOrderPriorityCat.MEDIUM: return 'Medium';
      case WorkOrderPriorityCat.HIGH: return 'High';
      case WorkOrderPriorityCat.CRITICAL: return 'Critical';
      default: return 'Medium';
    }
  }

  private getPriorityClass(priorityCat?: number) {
    switch (priorityCat) {
      case WorkOrderPriorityCat.LOW: return 'low';
      case WorkOrderPriorityCat.MEDIUM: return 'medium';
      case WorkOrderPriorityCat.HIGH: return 'high';
      case WorkOrderPriorityCat.CRITICAL: return 'critical';
      default: return 'medium';
    }
  }

  private openWorkOrder(wo: any) {
    const viewer = document.createElement('work-order-viewer') as any;
    viewer.open = true;
    viewer.workOrder = wo;
    viewer.companyId = this.companyId;
    viewer.firestore = db;
    document.body.appendChild(viewer);
    viewer.addEventListener('modal-closed', () => {
      document.body.removeChild(viewer);
      this.loadWorkOrders(); // Reload work orders after closing viewer
    }, { once: true });
  }

  private handleAddWorkOrder() {
    const modal = document.createElement('work-order-modal') as any;
    modal.open = true;
    modal.companyId = this.companyId;
    modal.firestore = db;
    
    // Pre-fill the asset_id in the formData
    const assetId = this.asset?.id || this.asset?.asset_id;
    modal.formData = {
      description: '',
      asset_id: assetId,
      priority_cat: WorkOrderPriorityCat.MEDIUM,
      status_cat: WorkOrderStatusCat.OPEN,
      assigned_to: ''
    };
    
    document.body.appendChild(modal);
    modal.addEventListener('modal-closed', () => {
      document.body.removeChild(modal);
      this.loadWorkOrders(); // Reload work orders after adding new one
    }, { once: true });
  }

  render() {
    return html`
      <div class="container">
        <div class="header-section">
          <div class="info-description">
            Work Orders associated with this asset
          </div>
          <sl-button variant="primary" size="small" @click=${this.handleAddWorkOrder}>
            <sl-icon slot="prefix" name="plus-circle"></sl-icon>
            Add Work Order
          </sl-button>
        </div>

        ${this.isLoading ? html`
          <div style="display:flex;justify-content:center;padding:1rem">
            <sl-spinner></sl-spinner>
          </div>
        ` : this.error ? html`
          <sl-alert variant="danger" open>${this.error}</sl-alert>
        ` : this.workOrders.length === 0 ? html`
          <div class="empty-state">
            No work orders for this asset
          </div>
        ` : html`
          <div class="data-table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th>WO ID</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Assigned To</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                ${this.workOrders.map((wo: any) => html`
                  <tr class="clickable-row" @click=${() => this.openWorkOrder(wo)}>
                    <td>${String(wo.id || wo.$id || '').slice(0, 8)}</td>
                    <td>${wo.model?.description || wo.description || 'No description'}</td>
                    <td>
                      <span class="status-badge ${this.getStatusClass(wo.model?.status_cat || wo.status_cat)}">
                        ${this.getStatusLabel(wo.model?.status_cat || wo.status_cat)}
                      </span>
                    </td>
                    <td>
                      <span class="priority-badge ${this.getPriorityClass(wo.model?.priority_cat || wo.priority_cat)}">
                        ${this.getPriorityLabel(wo.model?.priority_cat || wo.priority_cat)}
                      </span>
                    </td>
                    <td>${this.getUserName(wo.model?.assigned_to || wo.assigned_to)}</td>
                    <td>${this.formatDate(wo.model?.created_at || wo.created_at)}</td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        `}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asset-wos': AssetWOs;
  }
}

